/* [SWT] Hero Launch - carousel behaviour.
   Snap-to-slide drag on mouse and touch. Dots always; arrows on desktop hover.

   A section can hold several independent carousels: the combined desktop
   carousel, and (in split mobile mode) a posters-only and a products-only
   carousel that CSS reveals at 749px and below. Every [data-swt-hero-slider]
   inside the root is initialised on its own with its own index, timer and
   chrome. Hidden carousels tick harmlessly: display: none swallows their
   transforms and they cannot receive pointer events. Crossing the breakpoint
   needs no mode logic - the resize re-snap recalculates widths, and CSS
   swaps which carousels are visible.

   Dots and arrows live on the slider, outside the track, so they never
   translate away. Each slide declares, per breakpoint:
   - data-swt-hero-ondark-desk/-mob ("true" | "false"): chrome lands on artwork
   - data-swt-hero-stripdark-desk/-mob ("true" | "false"): the flat surface
     under the chrome is dark
   Liquid computes both. The section's own darkness is irrelevant here: what
   matters is the surface directly under the pill on the active slide.

   Autoplay pauses on hover and focus over the whole section. The arrows fade
   in on the same hover, so the two behaviours are two faces of one state. */

(function () {
  var DRAG_THRESHOLD = 0.15; // fraction of slide width needed to advance
  var CLICK_SLOP = 6; // px of movement still treated as a click
  var MOBILE_QUERY = '(max-width: 749px)';

  function initSlider(root, slider) {
    if (!slider || slider.dataset.swtHeroInit === 'true') return;
    slider.dataset.swtHeroInit = 'true';

    var track = slider.querySelector('[data-swt-hero-track]');
    var slides = Array.prototype.slice.call(slider.querySelectorAll('[data-swt-hero-slide]'));
    if (!track || slides.length === 0) return;

    var dots = Array.prototype.slice.call(slider.querySelectorAll('[data-swt-hero-dot]'));
    var dotsWrap = slider.querySelector('[data-swt-hero-dots]');
    var arrowsWrap = slider.querySelector('[data-swt-hero-arrows]');
    var prevBtn = slider.querySelector('[data-swt-hero-prev]');
    var nextBtn = slider.querySelector('[data-swt-hero-next]');

    var index = 0;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var mobileMQ = window.matchMedia(MOBILE_QUERY);
    var multi = slides.length > 1;
    var autoplayOn = root.dataset.autoplay === 'true' && !reduced && multi;
    var delay = (parseInt(root.dataset.delay, 10) || 6) * 1000;
    var timer = null;

    /* ---- rendering ---- */

    function setTranslate(px) {
      track.style.transform = 'translate3d(' + px + 'px, 0, 0)';
    }

    function slideWidth() {
      return slider.clientWidth || 1;
    }

    /* Chrome sits on artwork when the active slide has no below-strip at the
       current breakpoint (ondark). Otherwise it sits on a flat surface, and
       stripdark says whether that surface is dark. Both come from Liquid,
       per breakpoint, so a light section showing through an inset strip gets
       dark dots and an opaque ink strip gets light ones. */
    function syncChromeTheme() {
      var current = slides[index];
      if (!current) return;
      var mob = mobileMQ.matches;
      var onDark = current.dataset[mob ? 'swtHeroOndarkMob' : 'swtHeroOndarkDesk'] === 'true';
      var stripDark = current.dataset[mob ? 'swtHeroStripdarkMob' : 'swtHeroStripdarkDesk'] === 'true';

      if (dotsWrap) {
        dotsWrap.classList.toggle('swt-hero-launch__dots--over-media', onDark);
        dotsWrap.classList.toggle('swt-hero-launch__dots--on-dark', !onDark && stripDark);
      }

      if (arrowsWrap) {
        arrowsWrap.classList.toggle('swt-hero-launch__arrows--over-media', onDark);
        arrowsWrap.classList.toggle('swt-hero-launch__arrows--on-dark', !onDark && stripDark);
      }
    }

    function update() {
      setTranslate(-index * slideWidth());
      slides.forEach(function (slide, i) {
        var active = i === index;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        try {
          slide.inert = !active;
        } catch (e) {
          /* inert unsupported */
        }
      });
      dots.forEach(function (dot, i) {
        if (i === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
      syncChromeTheme();
    }

    function goTo(i) {
      index = (i + slides.length) % slides.length;
      update();
    }

    /* ---- autoplay ---- */

    function stopAutoplay() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function startAutoplay() {
      if (!autoplayOn) return;
      stopAutoplay();
      timer = setInterval(function () {
        goTo(index + 1);
      }, delay);
    }

    /* ---- dots ---- */

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goTo(parseInt(dot.dataset.swtHeroDot, 10) || 0);
        startAutoplay();
      });
    });

    /* ---- arrows. Visible only on desktop hover (CSS). Clicking must not
           restart autoplay: the pointer is still inside, so mouseleave is
           what resumes it. ---- */

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(index - 1);
        stopAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(index + 1);
        stopAutoplay();
      });
    }

    /* ---- drag: mouse + touch, snap to slide ---- */

    var dragging = false;
    var startX = 0;
    var startY = 0;
    var deltaX = 0;
    var axisLocked = false;
    var horizontal = false;
    var pointerId = null;

    if (multi) {
      slider.classList.add('swt-hero-launch__slider--draggable');
    }

    function onDown(e) {
      if (!multi) return;
      if (e.button !== undefined && e.button !== 0) return;
      /* Never start a drag from an arrow, or the click gets swallowed. */
      if (e.target.closest && e.target.closest('[data-swt-hero-arrows]')) return;
      dragging = true;
      axisLocked = false;
      horizontal = false;
      startX = e.clientX;
      startY = e.clientY;
      deltaX = 0;
      pointerId = e.pointerId;
      stopAutoplay();
    }

    function onMove(e) {
      if (!dragging || e.pointerId !== pointerId) return;

      var dx = e.clientX - startX;
      var dy = e.clientY - startY;

      if (!axisLocked) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        horizontal = Math.abs(dx) > Math.abs(dy);
        axisLocked = true;
        if (!horizontal) {
          dragging = false;
          return;
        }
        slider.classList.add('swt-hero-launch__slider--dragging');
        if (slider.setPointerCapture) {
          try {
            slider.setPointerCapture(pointerId);
          } catch (err) {
            /* ignore */
          }
        }
      }

      if (!horizontal) return;
      if (e.cancelable) e.preventDefault();

      deltaX = dx;

      var atStart = index === 0 && deltaX > 0;
      var atEnd = index === slides.length - 1 && deltaX < 0;
      var eased = atStart || atEnd ? deltaX * 0.35 : deltaX;

      setTranslate(-index * slideWidth() + eased);
    }

    function endDrag() {
      if (!dragging) {
        cleanupDrag();
        return;
      }
      dragging = false;

      if (!horizontal) {
        cleanupDrag();
        return;
      }

      var moved = Math.abs(deltaX);
      var threshold = slideWidth() * DRAG_THRESHOLD;

      if (moved > threshold) {
        goTo(deltaX < 0 ? index + 1 : index - 1);
      } else {
        update();
      }

      if (moved > CLICK_SLOP) {
        slider.addEventListener(
          'click',
          function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
          },
          { capture: true, once: true }
        );
      }

      cleanupDrag();
      startAutoplay();
    }

    function cleanupDrag() {
      slider.classList.remove('swt-hero-launch__slider--dragging');
      if (pointerId !== null && slider.releasePointerCapture) {
        try {
          slider.releasePointerCapture(pointerId);
        } catch (err) {
          /* ignore */
        }
      }
      pointerId = null;
      deltaX = 0;
      axisLocked = false;
      horizontal = false;
    }

    slider.addEventListener('pointerdown', onDown);
    slider.addEventListener('pointermove', onMove, { passive: false });
    slider.addEventListener('pointerup', endDrag);
    slider.addEventListener('pointercancel', function () {
      dragging = false;
      update();
      cleanupDrag();
      startAutoplay();
    });

    slider.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    /* ---- keyboard ---- */

    slider.addEventListener('keydown', function (e) {
      if (!multi) return;
      if (e.key === 'ArrowLeft') {
        goTo(index - 1);
        startAutoplay();
      } else if (e.key === 'ArrowRight') {
        goTo(index + 1);
        startAutoplay();
      }
    });

    /* ---- autoplay pausing. Hovering anywhere over the section pauses all
           of its carousels; each closure controls its own timer. ---- */

    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', startAutoplay);

    /* ---- breakpoint change: chrome theme may flip ---- */

    if (mobileMQ.addEventListener) {
      mobileMQ.addEventListener('change', syncChromeTheme);
    } else if (mobileMQ.addListener) {
      mobileMQ.addListener(syncChromeTheme);
    }

    /* ---- resize: re-snap without animating. Also fixes up a carousel that
           was measured while hidden (width 0) once it becomes visible. ---- */

    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        var prev = track.style.transition;
        track.style.transition = 'none';
        setTranslate(-index * slideWidth());
        void track.offsetWidth;
        track.style.transition = prev;
      });
    });

    /* ---- theme editor. Only the desktop copies carry shopify_attributes,
           so block-select lands on the combined carousel; the indexOf guard
           keeps the mobile carousels from reacting to foreign slides. ---- */

    document.addEventListener('shopify:block:select', function (e) {
      var slide = e.target.closest ? e.target.closest('[data-swt-hero-slide]') : null;
      if (!slide) return;
      var i = slides.indexOf(slide);
      if (i > -1) {
        stopAutoplay();
        goTo(i);
      }
    });

    update();
    startAutoplay();
  }

  function initHero(root) {
    if (!root || root.dataset.swtHeroInit === 'true') return;
    root.dataset.swtHeroInit = 'true';

    root.querySelectorAll('[data-swt-hero-slider]').forEach(function (slider) {
      initSlider(root, slider);
    });
  }

  function boot() {
    document.querySelectorAll('[data-swt-hero-root]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-swt-hero-root]');
    if (root) initHero(root);
  });
})();
